import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { delete_role, get_all_roles } from '../api/rolesApi';
import { toast } from 'react-toastify';
import { RoleListInterface } from '../../../modules/accounts/components/settings/SettingsModel';
import { KTIcon, KTSVG } from '../../../../_metronic/helpers';
import { permissionCheck } from '../../../helper-functions/PermissionCheck';
import { LoadingProgressBar } from '../../projects/components/LoadingProgressBar';

var role_create, role_update, role_delete, role_view, role_activity_log
function RolesList() {
  const [roleList, setRoleList] = useState<RoleListInterface[]>([]);
  const [selectedRole, setSelectedRole] = useState<RoleListInterface | null>(null)
  const queryClient = useQueryClient();
  const [searchInput, setSearchInput] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [sortBy, setSortBy] = useState<{ key: string, ascending: boolean } | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();
  const [visibleData, setVisibleData] = useState<RoleListInterface[]>([]);
  const [selectedStatus, setSelectedStatus] = useState('');
  const { data, isLoading, isError } = useQuery('roles', get_all_roles, { refetchOnWindowFocus: false });
  useEffect(() => {
    if (data) {
      setRoleList(data.data)
    }
  }, [data]);
  role_create = Boolean(permissionCheck('role-create'));
  role_update = Boolean(permissionCheck('role-update'));
  role_delete = Boolean(permissionCheck('role-delete'));
  role_view = Boolean(permissionCheck('role-view'));
  role_activity_log = Boolean(permissionCheck('role-activity-log'));
  const totalItems = roleList.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const pageSafe = Math.min(currentPage, totalPages);
  const startIdx = (pageSafe - 1) * pageSize;
  const endIdx = startIdx + pageSize;
  
  useEffect(() => {
    if (roleList.length > 0) {
      let sortedData = [...roleList];
      if (sortBy) {
        sortedData.sort((a, b) => {
          if (sortBy && sortBy.key === 'status' && typeof a.status === 'number' && typeof b.status === 'number') {
            return sortBy.ascending ? a.status - b.status : b.status - a.status;
          } else {
            const aValue = (a[sortBy?.key] || '') as string;
            const bValue = (b[sortBy?.key] || '') as string;
            return sortBy && sortBy.ascending ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
          }
        });
      }
      setVisibleData(
        sortedData.slice(startIdx, endIdx)
      );
    }
  }, [roleList, currentPage, pageSize, sortBy, startIdx, endIdx]);
  
  useEffect(() => {
    setCurrentPage(1);
  }, [pageSize]);
  const handleInputChange = (e: React.FormEvent<HTMLInputElement>) => {
    const searchTerm = e.currentTarget.value;
    setSearchInput(searchTerm);
    const filteredItems = data.data.filter((role) => {
      return role.name?.toLowerCase().includes(searchTerm.toLowerCase());
    })

    setRoleList(filteredItems);
  };
  const handleDeleteRole = useMutation((id: number) => {
    return delete_role(id);
  }, {
    onSuccess: (response: any) => {
      toast.success(response?.message || 'Role deleted successfully', {
        position: 'top-right',
        autoClose: 3000,
      });
      // Invalidate and refetch roles list
      queryClient.invalidateQueries('roles');
      // Also refetch manually to update the list
      get_all_roles()
        .then((data) => {
          setRoleList(data.data);
        })
        .catch((error: any) => {
          console.error('Error refetching roles:', error);
        });
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || 'Failed to delete role';
      toast.error(errorMessage, {
        position: 'top-right',
        autoClose: 3000,
      });
    },
  });
  // Handle Sort
  const handleSort = (key: string) => {
    setSortBy((prev) =>
      !prev || prev.key !== key
        ? { key, ascending: true } // Sort ascending for a new key
        : { key, ascending: !prev.ascending } // Toggle sorting for the same key
    );
  };
  // Handle for Update Role
  const handleUpdate = (role: any) => {
    navigate("/roles/edit-role", { state: { role: role } });
  };
  const handleRoleLogs = (role: any) => {
    navigate("/roles/role-logs", { state: { id: role.id } });
  };
  return (
    <>
      {/* Loading Progress Bar */}
      <LoadingProgressBar isLoading={isLoading || handleDeleteRole.isLoading} />
      
      <div className='card card-xxl-stretch mx-4 mb-5 mb-xl-10'>
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bold fs-3 mb-1'>List of Roles</span>
        </h3>
        {
          role_create && (
            <div
              className='card-toolbar'
            >
              <button
                data-toggle="tooltip" data-placement="bottom" title="Navigate to Add Role"
                className='btn btn-sm btn-light-dark'
                onClick={() => navigate('/roles/add-role')}
              >
                <KTIcon iconName='plus' className='fs-3' />
                Add Role
              </button>
            </div>
          )
        }
      </div>
      <div className='card-header border-0 pt-5'>
        {/* begin::Search */}
        <h3 className='card-title align-items-start flex-column'>
          <KTIcon iconName='magnifier' className='fs-1 position-absolute ms-6' />
          <input
            data-toggle="tooltip" data-placement="bottom" title="Search a Role"
            type='text'
            data-kt-user-table-filter='search'
            className='form-control form-control-solid w-250px ps-14'
            placeholder='Search Role'
            value={searchInput}
            onChange={handleInputChange}
          />
        </h3>
        {/* end::Search */}
        {/* commenting for future use  */}
        {/* <div
            className='card-toolbar'
          >
            <h5 className='p-3'>Filter by:</h5>
            <div>
              <select
                className='form-select form-select-solid form-select-lg fw-bold'
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
              >
                <option value=''>Filter by Status...</option>
                <option value={1}>Active</option>
                <option value={0}>Inactive</option>
              </select>
            </div>
          </div> */}
      </div>
      <div className='card-body py-3'>
        <div className='table-responsive'>
          <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
            <thead>
              <tr className='fw-bold text-muted'>
                <th className='min-w-150px'>Role Title <span onClick={() => handleSort('name')}> <KTIcon iconName='up-down' className='fs-4 cursor-pointer' /></span></th>
                <th className='min-w-150px'>Status <span onClick={() => handleSort('status')}> <KTIcon iconName='up-down' className='fs-4 cursor-pointer' /></span></th>
                <th className='min-w-140px'>Updated By <span onClick={() => handleSort('updated_by_name')}> <KTIcon iconName='up-down' className='fs-4 cursor-pointer' /></span></th>
                <th className='min-w-140px'>Updated At <span onClick={() => handleSort('updated_at')}> <KTIcon iconName='up-down' className='fs-4 cursor-pointer' /></span></th>
                <th className='min-w-100px text-center'>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isError ? (
                <tr>
                  <td>Error loading data. Please try again later.</td>
                </tr>
              ) : isLoading ? (
                // Skeleton Loading for Roles Table
                <>
                  {[1, 2, 3, 4, 5].map((skeletonIndex) => (
                    <tr key={skeletonIndex}>
                      <td>
                        <div className='d-flex align-items-center'>
                          <div className='d-flex justify-content-start flex-column'>
                            <div className='placeholder placeholder-lg bg-light w-150px'></div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className='d-flex align-items-center'>
                          <div className='d-flex justify-content-start flex-column'>
                            <div className='placeholder placeholder-lg bg-light w-80px h-25px rounded'></div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className='d-flex flex-column w-100 me-2'>
                          <div className='d-flex flex-stack mb-2'>
                            <div className='placeholder placeholder-sm bg-light w-120px'></div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className='d-flex flex-column w-80 me-2'>
                          <div className='d-flex flex-stack mb-2'>
                            <div className='placeholder placeholder-sm bg-light w-150px'></div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className='d-flex justify-content-center flex-shrink-0'>
                          <div className='placeholder placeholder-sm bg-light w-30px h-30px rounded me-1'></div>
                          <div className='placeholder placeholder-sm bg-light w-30px h-30px rounded me-1'></div>
                          <div className='placeholder placeholder-sm bg-light w-30px h-30px rounded'></div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </>
              ) : roleList.length > 0 ? (
                visibleData.map((role) => (
                  <tr key={role.id}>
                    <td>
                      <div className='d-flex align-items-center'>
                        <div className='d-flex justify-content-start flex-column'>
                          <h3 className='text-dark fw-bold fs-6'>
                            {role.name}
                          </h3>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className='d-flex align-items-center'>
                        <div className='d-flex justify-content-start flex-column'>
                          <h3
                            className={`fw-bold fs-6 badge ${role.status === 1 ? 'badge-success' : 'badge-danger'}`}
                          >
                            {role.status === 1 ? 'Active' : 'Inactive'}
                          </h3>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className='d-flex flex-column w-100 me-2'>
                        <div className='d-flex flex-stack mb-2'>
                          {role.updated_by_name}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className='d-flex flex-column w-80 me-2'>
                        <div className='d-flex flex-stack mb-2'>
                          {role.updated_at ? new Date(role.updated_at).toLocaleDateString() : 'N/A'}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className='d-flex justify-content-center flex-shrink-0'>
                        {/* {role_activity_log && (
                          <button
                            onClick={() => {
                              handleRoleLogs(role)
                            }}
                            className='btn btn-icon btn-light-dark btn-active-color-warning btn-sm me-1'
                            data-bs-toggle='tooltip'
                            data-bs-placement='top'
                            data-bs-custom-class='tooltip-dark'
                            title='Role Logs'
                          >
                            <i className='bi bi-journal-text'></i>
                          </button>
                        )} */}
                        {role_update && (
                          <button
                            onClick={() => handleUpdate(role)}
                            data-toggle="tooltip" data-placement="bottom" title="Edit Role"
                            className='btn btn-icon btn-light-dark btn-active-color-warning btn-sm me-1'
                          >
                            <KTIcon iconName='pencil' className='fs-3' />
                          </button>
                        )}
                        {role_delete && (
                          <button
                            data-toggle="tooltip" data-placement="bottom" title="Delete Role"
                            onClick={() => setSelectedRole(role)}
                            className='btn btn-icon btn-light-dark btn-active-color-danger btn-sm'
                            data-bs-toggle='modal'
                            data-bs-target='#kt_modal_delete_role'
                          >
                            <KTIcon iconName='trash' className='fs-3' />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td>No Roles Available.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {/* Pagination controls */}
      <div className='d-flex align-items-center justify-content-between mt-4 py-4 px-4'>
        <div className='d-flex align-items-center gap-3'>
          <span className='text-muted fs-7'>
            Showing {roleList.length === 0 ? 0 : startIdx + 1}-{Math.min(endIdx, roleList.length)} of {roleList.length}
          </span>
          <div className='d-flex align-items-center'>
            <span className='me-2 text-muted fs-7'>Show</span>
            <select
              className='form-select form-select-sm w-auto'
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
            <span className='ms-2 text-muted fs-7'>per page</span>
          </div>
        </div>
        <ul className='pagination mb-0'>
          <li className={`page-item ${pageSafe === 1 ? 'disabled' : ''}`}>
            <button className='page-link' onClick={() => setCurrentPage(1)}>«</button>
          </li>
          <li className={`page-item ${pageSafe === 1 ? 'disabled' : ''}`}>
            <button className='page-link' onClick={() => setCurrentPage(p => Math.max(1, p - 1))}>Prev</button>
          </li>
          <li className='page-item active'>
            <span className='page-link'>{pageSafe} / {totalPages}</span>
          </li>
          <li className={`page-item ${pageSafe === totalPages ? 'disabled' : ''}`}>
            <button className='page-link' onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}>Next</button>
          </li>
          <li className={`page-item ${pageSafe === totalPages ? 'disabled' : ''}`}>
            <button className='page-link' onClick={() => setCurrentPage(totalPages)}>»</button>
          </li>
        </ul>
      </div>
      {/* Delete Role Confirmation Modal */}
      <div className='modal fade' tabIndex={-1} id='kt_modal_delete_role'>
        <div className='modal-dialog'>
          <div className='modal-content'>
            <div className='modal-header'>
              <h5 className='modal-title'>Delete Role Confirmation</h5>
              <div
                className='btn btn-icon btn-sm btn-active-light-primary ms-2'
                data-bs-dismiss='modal'
                aria-label='Close'
              >
                <KTSVG
                  path='/media/icons/duotune/arrows/arr061.svg'
                  className='svg-icon svg-icon-2x'
                />
              </div>
            </div>
            <div className='modal-body'>
              {selectedRole ? (
                <>
                  <p className='mb-2'>
                    <span className='fw-bold fs-5'>Role Title:</span> {selectedRole.name}
                  </p>
                </>
              ) : null}
            </div>
            <div className='modal-footer'>
              <button type='button' className='btn btn-light' data-bs-dismiss='modal'>
                Close
              </button>
              <button
                type='button'
                className='btn btn-danger'
                onClick={() => {
                  if (selectedRole) {
                    handleDeleteRole.mutate(selectedRole.id);
                  }
                }}
                data-bs-dismiss='modal'
                disabled={handleDeleteRole.isLoading}
              >
                {handleDeleteRole.isLoading ? (
                  <>
                    Deleting...
                    <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                  </>
                ) : (
                  'Delete Role'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      </div>
    </>
  )
};

export default RolesList